import {
  DecorFacetsResponseSchema,
  DecorListResponseSchema,
  DecorSchema,
  type AlternativeMode,
  type Decor,
  type DecorFacetsResponse,
  type DecorKind,
  type DecorListResponse,
} from '../contracts/decors';
import {
  DecorMatchRequestSchema,
  DecorMatchResponseSchema,
  type DecorMatchRequest,
  type DecorMatchResponse,
} from '../contracts/decor-match';
import { ShopsResponseSchema, type ShopsResponse } from '../contracts/handles';
import type { QueryValue, RequestFn } from './client';

/**
 * Namespace `client.decors` — обёртки над эндпоинтами Блока 2 «Декор».
 * Контракт: PRD `docs/prd/v4/04-block-2-decor.md` §5.
 *
 * `kind` здесь — routing parameter (определяет URL `/api/decors/${kind}/...`),
 * НЕ filter discriminator. Это согласуется с ADR-0007 «shops shared module»:
 * P-033 запрещает kind в filter functions на attributes, но позволяет в URL
 * builders и shared-роутинге. ESLint правило `no-shared-decor-kind-handlers`
 * этот файл не блокирует (URL-construction, не атрибут-фильтрация).
 */

/** Loose query input для list — strict валидация per kind на стороне MSW/Edge. */
export interface DecorListQueryInput {
  subcategories?: readonly string[];
  brands?: readonly string[];
  types?: readonly string[];
  /** Поиск по названию (ADR-0067): подстрока name+brand. Композится с фильтром
   *  через AND. `undefined`/пусто → не шлётся (buildUrl опускает undefined). */
  name?: string;
  cursor?: string;
  limit?: number;
}

export interface DecorsApi {
  list(kind: DecorKind, query?: DecorListQueryInput): Promise<DecorListResponse>;
  /**
   * Опции фасета фильтра ИЗ ДАННЫХ (ADR-0054/0059): непустые подкатегории kind +
   * distinct бренды (скоуп `subcategories`) + типы (скоуп `subcategories`+`brands`,
   * каскад Бренд→Тип). `kind`/`subcategories`/`brands` — routing/scoping, не
   * attribute-фильтрация per kind (P-033 не затрагивается).
   */
  facets(
    kind: DecorKind,
    subcategories?: readonly string[],
    brands?: readonly string[],
  ): Promise<DecorFacetsResponse>;
  get(kind: DecorKind, globalId: string): Promise<Decor>;
  match(request: DecorMatchRequest): Promise<DecorMatchResponse>;
  /**
   * Sensitive: требует JWT (401 для гостя). ADR-0060 §3: `city` обязателен —
   * бэк отдаёт только дилеров этого города с брендом декора в `brands` (иначе
   * `{ shops: [] }`, empty-состояние). Список городов — `dealers.cities()`.
   */
  shops(kind: DecorKind, globalId: string, city: string): Promise<ShopsResponse>;
  /**
   * Альтернативы декора по LAB-сдвигу (Экран 2.2.8). ADR-0055: пул сужается
   * активным фильтром (`subcategories/brands/types`); без фильтра бэк fallback'ит
   * на подкатегорию исходного декора. `filter` — опционален, поля повторяют list.
   */
  alternatives(
    kind: DecorKind,
    globalId: string,
    mode: AlternativeMode,
    filter?: DecorAlternativesFilter,
  ): Promise<DecorListResponse>;
}

/** Активный фильтр для скоупа альтернатив (ADR-0055). Поля — как у list-фильтра. */
export interface DecorAlternativesFilter {
  subcategories?: readonly string[];
  brands?: readonly string[];
  types?: readonly string[];
}

export function createDecorsApi(request: RequestFn): DecorsApi {
  return {
    list: (kind, query = {}) =>
      request({
        method: 'GET',
        path: `/api/decors/${encodeURIComponent(kind)}`,
        query: queryToParams(query),
        responseSchema: DecorListResponseSchema,
      }),

    facets: (kind, subcategories, brands) =>
      request({
        method: 'GET',
        path: `/api/decors/${encodeURIComponent(kind)}/facets`,
        query: { subcategories, brands },
        responseSchema: DecorFacetsResponseSchema,
      }),

    get: (kind, globalId) =>
      request({
        method: 'GET',
        path: `/api/decors/${encodeURIComponent(kind)}/${encodeURIComponent(globalId)}`,
        responseSchema: DecorSchema,
      }),

    match: (req) => {
      // Зарекомендованная защита P-009: даже если type-checker обойдён,
      // .strict() блокирует extra поля типа photoBlob/base64. См. ADR-0009.
      // Возвращаем rejected Promise (не sync throw) — клиент-API всегда async.
      const parsed = DecorMatchRequestSchema.safeParse(req);
      if (!parsed.success) {
        return Promise.reject(parsed.error);
      }
      return request({
        method: 'POST',
        path: '/api/decors/match',
        body: parsed.data,
        responseSchema: DecorMatchResponseSchema,
      });
    },

    shops: (kind, globalId, city) =>
      request({
        method: 'GET',
        path: `/api/decors/${encodeURIComponent(kind)}/${encodeURIComponent(globalId)}/shops`,
        query: { city },
        responseSchema: ShopsResponseSchema,
        auth: true,
      }),

    alternatives: (kind, globalId, mode, filter) =>
      request({
        method: 'GET',
        path: `/api/decors/${encodeURIComponent(kind)}/${encodeURIComponent(globalId)}/alternatives`,
        query: {
          mode,
          subcategories: filter?.subcategories,
          brands: filter?.brands,
          types: filter?.types,
        },
        responseSchema: DecorListResponseSchema,
      }),
  };
}

function queryToParams(q: DecorListQueryInput): Record<string, QueryValue> {
  return {
    subcategories: q.subcategories,
    brands: q.brands,
    types: q.types,
    name: q.name,
    cursor: q.cursor,
    limit: q.limit !== undefined ? Number(q.limit) : undefined,
  };
}
